/**
 * Sprint 22 — chat session history hooks.
 *
 * Wrap the four chat-session MCP tools (list/get/archive/delete_chat_session)
 * for the SessionHistorySidebar. Agent-scoped sessions list by node_id (catching
 * legacy NULL-scope conversations too); workspace/entity/table/page sessions list
 * by (scope_kind, scope_id).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { UIMessage } from "@ai-sdk/react";
import { mcpClient } from "../../lib/mcp-client";
import { queryKeys } from "../../lib/query-keys";
import type { ChatScope } from "../components/chat/ChatRailContext";

export interface ChatSession {
  id: string;
  node_id: string;
  workspace_id: string;
  title: string;
  scope_kind: string | null;
  scope_id: string | null;
  scope_type_slug: string | null;
  created_at: string;
  updated_at: string;
  archived_at: string | null;
}

function unwrap<T>(r: { isError?: boolean; structuredContent?: T; content?: Array<{ text?: string }> }): T {
  if (r.isError) throw new Error(r.content?.[0]?.text ?? "Request failed");
  return r.structuredContent as T;
}

/** The scope-key used both for the list args and the query cache key. */
export function chatScopeKey(scope: ChatScope): string {
  return `${scope.kind}:${scope.id ?? ""}`;
}

function listArgs(scope: ChatScope, limit: number): Record<string, unknown> {
  // Agent scope lists by node_id so legacy (scope_kind=NULL) conversations show too.
  if (scope.kind === "agent" && scope.id) return { node_id: scope.id, limit };
  if (scope.kind === "workspace") return { scope_kind: "workspace", limit };
  if (scope.id) return { scope_kind: scope.kind, scope_id: scope.id, limit };
  return { scope_kind: scope.kind, limit };
}

export function useChatSessions(scope: ChatScope, limit = 50, enabled = true) {
  return useQuery<ChatSession[]>({
    queryKey: queryKeys.chatSessions(chatScopeKey(scope)),
    queryFn: async () => {
      const r = await mcpClient.toolsCall<{ conversations: ChatSession[] }>(
        "list_chat_sessions",
        listArgs(scope, limit),
      );
      return unwrap(r).conversations ?? [];
    },
    enabled,
    staleTime: 15_000,
    refetchInterval: 30_000,
  });
}

/** Imperatively load a session's full message history (for resume). */
export async function loadChatSession(sessionId: string): Promise<UIMessage[]> {
  const r = await mcpClient.toolsCall<{ messages: UIMessage[] }>("get_chat_session", {
    session_id: sessionId,
  });
  return unwrap(r).messages ?? [];
}

export function useArchiveChatSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const r = await mcpClient.toolsCall("archive_chat_session", { session_id: sessionId });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Archive failed");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-sessions"] }),
  });
}

export function useDeleteChatSession() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const r = await mcpClient.toolsCall("delete_chat_session", { session_id: sessionId });
      if (r.isError) throw new Error(r.content?.[0]?.text ?? "Delete failed");
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-sessions"] }),
  });
}
